import Keycloak, { type KeycloakConfig } from 'keycloak-js';
import * as Sentry from '@sentry/react';
import { isTransientAuthError } from './authErrors';

const buildConfig = (): KeycloakConfig => ({
  url: import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8180',
  realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'lutiq-dashboard',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'lutiq-dashboard-spa',
});

const keycloak = new Keycloak(buildConfig());

type InitPhase = 'idle' | 'pending' | 'success' | 'failed';

let initState: {
  phase: InitPhase;
  promise: Promise<boolean> | null;
} = { phase: 'idle', promise: null };

// Bounded per-tab counter that prevents an infinite recover → reload loop
// when Keycloak itself is broken. Stored in sessionStorage so a reload
// preserves the budget within a single tab but doesn't leak across tabs.
const RECOVERY_STORAGE_KEY = 'lutiq:keycloak:recovery-attempts';
const MAX_RECOVERY_ATTEMPTS = 2;

// OAuth 2.0 / OIDC callback params (RFC 6749 + OIDC Core). Stripped from the
// captured browser URL before building the `redirect_uri` we hand to Keycloak
// so stale callback params can never contaminate a fresh authorize request.
//
// Deliberately NOT shared with sentry.ts's SENSITIVE_QUERY_PARAMS: that set
// includes `token`, which the /accept-invite?token=… flow depends on. Mixing
// the two lists would break invite links on recovery redirects.
const OAUTH_QUERY_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
  'error_uri',
] as const;

const stripOAuthParams = (search: string): string => {
  if (!search) return '';
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const key of OAUTH_QUERY_PARAMS) {
    params.delete(key);
  }
  const cleaned = params.toString();
  return cleaned ? `?${cleaned}` : '';
};

const stripOAuthFragment = (hash: string): string => {
  if (!hash) return '';
  // Bare-anchor fragments (`#section`) must pass through untouched.
  // URLSearchParams('section') would parse that as a one-key empty value
  // and round-trip as `section=`, corrupting anchor-based navigation.
  if (!hash.includes('=')) return hash;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  for (const key of OAUTH_QUERY_PARAMS) {
    params.delete(key);
  }
  const cleaned = params.toString();
  return cleaned ? `#${cleaned}` : '';
};

export const buildRedirectUri = (path?: string): string => {
  if (path !== undefined) {
    return `${window.location.origin}${path}`;
  }

  const cleanedSearch = stripOAuthParams(window.location.search);
  const cleanedHash = stripOAuthFragment(window.location.hash);
  return `${window.location.origin}${window.location.pathname}${cleanedSearch}${cleanedHash}`;
};

// Test-only helper — lets unit tests reset the module singleton between cases.
// Production code must never call this.
export const __resetForTests = (): void => {
  initState = { phase: 'idle', promise: null };
  try {
    sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const getKeycloak = () => keycloak;

// Rewrites the current URL via history.replaceState so any leftover OAuth
// callback params (`?code=…`, `#state=…`, etc.) are gone BEFORE we hand
// control to keycloak-js. Required because keycloak-js reads window.location
// internally when constructing CallbackStorage entries for the next round
// trip; leaving stale params in place races the fresh state we're issuing.
const cleanCurrentUrl = (): void => {
  const cleaned = buildRedirectUri();
  if (cleaned !== window.location.href) {
    window.history.replaceState(null, '', cleaned);
  }
};

const attemptRecovery = async (): Promise<never> => {
  let attempts = 0;
  try {
    // Clamp to 0 for any non-finite / negative value. Otherwise NaN (from a
    // bogus sessionStorage value) slips past `>= MAX_RECOVERY_ATTEMPTS`
    // because `NaN >= 2` is false, letting the loop guard silently bypass.
    const raw = Number(sessionStorage.getItem(RECOVERY_STORAGE_KEY));
    attempts = Number.isFinite(raw) && raw >= 0 ? raw : 0;
  } catch {
    // sessionStorage unavailable — treat as zero and fall through.
  }
  if (attempts >= MAX_RECOVERY_ATTEMPTS) {
    // Break the loop: drop the counter so the NEXT user-initiated login
    // attempt starts with a fresh budget, and let AuthContext surface the
    // terminal state via the tagged error.
    try {
      sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
    } catch {
      // ignore
    }
    throw new Error('keycloak-recovery-loop-guard-tripped');
  }
  try {
    sessionStorage.setItem(RECOVERY_STORAGE_KEY, String(attempts + 1));
  } catch {
    // ignore
  }

  cleanCurrentUrl();
  initState = { phase: 'idle', promise: null };

  // No `prompt: 'login'` — the user's Keycloak SSO session cookie may still
  // be valid; a PKCE/state mismatch is a client-side problem and doesn't
  // justify forcing a credential re-entry.
  await keycloak.login({ redirectUri: buildRedirectUri('/') });
  // In production keycloak.login() navigates the browser away, so this
  // throw is unreachable. In tests (and as a defensive fallback) the throw
  // tags the promise so AuthContext can suppress the loading→false flicker.
  throw new Error('keycloak-recovery-redirect-pending');
};

// Keycloak-js's createUUID() calls window.crypto.getRandomValues(), which is
// only exposed on secure contexts (https://, localhost, file://). On insecure
// http:// origins the constructor throws `Error: Web Crypto API is not
// available.` from deep inside keycloak-js — LUTIQ-1Q/1P/1N. The dashboard is
// only served over https in prod + staging, so the only way to hit this path
// is a direct http:// visit; the right fix is to bounce the browser to https
// before any auth code runs. SSR guard keeps Vitest/jsdom happy.
const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

const ensureSecureContext = (): void => {
  if (typeof window === 'undefined') return;
  if (window.isSecureContext) return;
  if (window.location.protocol === 'https:') return;
  // Browsers treat http://localhost as a secure context (W3C Secure Contexts
  // §3.1). jsdom doesn't set `isSecureContext`, so rely on the hostname check
  // so our test suite keeps running against http://localhost without the
  // production redirect firing.
  if (LOCALHOST_HOSTNAMES.has(window.location.hostname)) return;

  const httpsUrl = `https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(httpsUrl);
  // Throw so callers short-circuit the current microtask instead of racing the
  // navigation. AuthContext.boot maps this message to the recovery-pending
  // branch, keeping the spinner on screen through the bounce.
  throw new Error('keycloak-recovery-redirect-pending');
};

export const initKeycloak = (): Promise<boolean> => {
  ensureSecureContext();
  if (initState.phase === 'success' || initState.phase === 'pending') {
    return initState.promise!;
  }
  const promise: Promise<boolean> = keycloak
    .init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      redirectUri: buildRedirectUri(),
      checkLoginIframe: false,
    })
    .then((authenticated) => {
      initState = { phase: 'success', promise };
      // Successful login invalidates any earlier recovery attempt budget.
      try {
        sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
      } catch {
        // sessionStorage unavailable (SSR / privacy mode) — ignore.
      }
      return authenticated;
    })
    .catch(async (err) => {
      // A transient network blip during the OAuth code→token exchange (very
      // common on mobile WebKit, which rejects fetch() with "Load failed")
      // isn't actionable — attemptRecovery() below re-drives the flow. Only
      // capture a real, persistent init failure; breadcrumb the blips so they
      // don't open a Sentry issue for every flaky-network mobile user
      // (LUTIQ-60). The classifier is shared with AuthContext's boot path.
      if (isTransientAuthError(err)) {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'keycloak-init transient network failure (recovery scheduled)',
        });
      } else {
        Sentry.captureException(err, { tags: { component: 'keycloak-init' } });
      }
      initState = { phase: 'failed', promise };
      return attemptRecovery();
    });
  initState = { phase: 'pending', promise };
  return promise;
};

export const isKeycloakInitialized = (): boolean => initState.phase === 'success';

/**
 * Escape hatch for mid-session recovery — `onAuthError` /
 * `onAuthRefreshError` fire *after* a successful init, so calling
 * `initKeycloak()` from them is a no-op (the state machine short-circuits
 * on `phase === 'success'`). This helper resets the singleton first so the
 * next attempt actually hits the init/catch path and the recovery ladder.
 *
 * Rejects with `keycloak-recovery-redirect-pending` or
 * `keycloak-recovery-loop-guard-tripped` the same way `initKeycloak()`
 * does, so callers can `.catch(() => {})` and let AuthContext surface the
 * outcome through the existing handlers.
 */
export const forceRecovery = async (): Promise<never> => {
  initState = { phase: 'idle', promise: null };
  return attemptRecovery();
};

export const ensureFreshToken = async (): Promise<string | null> => {
  // A cold load triggered by `onTokenExpired` (e.g. coming back to an idle
  // tab) hits this path before `initKeycloak()` is ever called, so the
  // module-entry guard in `initKeycloak` alone isn't enough to fully close
  // LUTIQ-1Q/1P/1N. keycloak-js's silent refresh still calls `createUUID()`
  // on the authorize fallback; mirror the guard here so every path into
  // keycloak-js runs from a secure context.
  ensureSecureContext();
  if (initState.phase !== 'success' || !keycloak.authenticated) {
    return null;
  }

  try {
    await keycloak.updateToken(30);
  } catch {
    return null;
  }

  return keycloak.token ?? null;
};

export const loginWithGoogle = async (redirectPath?: string): Promise<void> => {
  ensureSecureContext();
  const idpHint = import.meta.env.VITE_KEYCLOAK_GOOGLE_IDP_HINT;
  // buildRedirectUri() is OAuth-param-clean even when window.location is
  // contaminated, so callers never need to sanitise up front.
  await keycloak.login({
    redirectUri: buildRedirectUri(redirectPath),
    ...(idpHint ? { idpHint } : {}),
  });
};

export const loginWithPassword = async (redirectPath?: string): Promise<void> => {
  ensureSecureContext();
  await keycloak.login({
    redirectUri: buildRedirectUri(redirectPath),
  });
};

export const logout = async (): Promise<void> => {
  await keycloak.logout({
    redirectUri: buildRedirectUri('/'),
  });
};
